import type { IconSvgElement } from "@hugeicons/react";
import {
  Bug02Icon,
  Call02Icon,
  CallIncoming04Icon,
  CallOutgoing04Icon,
  ChartColumnIcon,
  Clock02Icon,
  DashboardSquare02Icon,
  Database02Icon,
  DatabaseLightningIcon,
  HelpSquareIcon,
  Key01Icon,
  LayoutGridIcon,
  Mortarboard02Icon,
  PackageIcon,
  Settings01Icon,
  SparklesIcon,
  UserMultiple03Icon,
} from "@hugeicons/core-free-icons";

export interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: IconSvgElement;
}

export interface NavGroup {
  name: string;
  items: NavItem[];
}

export interface NavGroups {
  admin: NavGroup[];
  user: NavGroup[];
}

/**
 * Top-level navigation groups for the application sidebar.
 *
 * Mirrors `voicebot-ui-redesign/project/src/shell.jsx` NAV_GROUPS, but
 * drops `knowledge-qa` (out of scope) and routes points at concrete
 * paths owned by the flat router.
 */
export const NAV_GROUPS: NavGroups = {
  admin: [
    {
      name: "Live",
      items: [
        {
          id: "dashboard",
          label: "Dashboard",
          path: "/monitoring/dashboard",
          icon: DashboardSquare02Icon,
        },
        {
          id: "one-call",
          label: "One Call",
          path: "/monitoring/one-call",
          icon: CallIncoming04Icon,
        },
        {
          id: "one-to-many",
          label: "Multi-Call",
          path: "/monitoring/one-to-many",
          icon: LayoutGridIcon,
        },
      ],
    },
    {
      name: "Configuration",
      items: [
        {
          id: "config-sets",
          label: "Config Sets",
          path: "/admin/config-sets",
          icon: Settings01Icon,
        },
        {
          id: "faq-sets",
          label: "FAQ Sets",
          path: "/admin/faq-sets",
          icon: HelpSquareIcon,
        },
        {
          id: "transfers",
          label: "Transfers",
          path: "/admin/transfers",
          icon: CallOutgoing04Icon,
        },
        {
          id: "call-groups",
          label: "Call Groups",
          path: "/admin/call-groups",
          icon: UserMultiple03Icon,
        },
        {
          id: "sip-numbers",
          label: "SIP Numbers",
          path: "/admin/sip-numbers",
          icon: Call02Icon,
        },
        {
          id: "avatars-3d",
          label: "3D Avatars",
          path: "/admin/avatars-3d",
          icon: PackageIcon,
        },
        {
          id: "avatars-liveavatar",
          label: "Liveavatar",
          path: "/admin/avatars-liveavatar",
          icon: SparklesIcon,
        },
      ],
    },
    {
      name: "Data Management",
      items: [
        {
          id: "customers",
          label: "Customers",
          path: "/admin/customers",
          icon: Database02Icon,
        },
        {
          id: "call-history",
          label: "Call History",
          path: "/admin/call-history",
          icon: Clock02Icon,
        },
        {
          id: "learnings",
          label: "Learnings",
          path: "/admin/learnings",
          icon: Mortarboard02Icon,
        },
        {
          id: "voice-auth-admin",
          label: "Voice Auth",
          path: "/admin/voice-auth",
          icon: Key01Icon,
        },
        {
          id: "statistics",
          label: "Statistics",
          path: "/admin/statistics",
          icon: ChartColumnIcon,
        },
      ],
    },
    {
      name: "Development",
      items: [
        {
          id: "debug",
          label: "Debug",
          path: "/admin/debug",
          icon: Bug02Icon,
        },
        {
          id: "database",
          label: "Database",
          path: "/admin/database",
          icon: DatabaseLightningIcon,
        },
      ],
    },
  ],
  user: [
    {
      name: "Monitoring",
      items: [
        {
          id: "dashboard",
          label: "Dashboard",
          path: "/monitoring/dashboard",
          icon: DashboardSquare02Icon,
        },
        {
          id: "one-call",
          label: "One Call",
          path: "/monitoring/one-call",
          icon: CallIncoming04Icon,
        },
        {
          id: "one-to-many",
          label: "Multi-Call",
          path: "/monitoring/one-to-many",
          icon: LayoutGridIcon,
        },
      ],
    },
  ],
};

/**
 * Routes where the sidebar may collapse. All other routes force expansion.
 */
export const MONITORING_PATHS = [
  "/monitoring/dashboard",
  "/monitoring/one-call",
  "/monitoring/one-to-many",
] as const;
